import MarkdownIt from 'markdown-it';
import sanitizeHtml from './sanitize.utils.js';
import highlight from './highlight.utils.js';
import { rendererPlugins, usePlugins } from '../plugins/index.js';
import { DEFAULT_LOCALE } from '../constants/locale.js';
import { DEFAULT_THEME } from '../constants/theme.js';

/**
 * The default settings for markdown.   They enable most of the options to allow the author
 * as much flexibility as possible.
 *
 * @typedef {Object} MarkdownSetting
 * @property {boolean} [html=false] - Allows HTML tags in the source
 * @property {boolean} [xhtmlOut=false] - Use '/' to close single tags br />
 * @property {boolean} [breaks=false] - Changes all \n to break tags
 * @property {boolean} [linkify=false] - Autoconvert URL-like text to links
 * @property {boolean} [typographer=false] - Enable some language-neutral replacement + quotes beautification
 * @property {string|[string]} [quotes='“”‘’'] - Double + single quotes replacement pairs, when typographer enabled and
 *                                               smartquotes on
 */
const defaultSettings = {
  // ../markdown/plugins/SanitizeHTML sanitizes the input to allow an approved list of tags
  html: true,
  xhtmlOut: true,
  breaks: false,
  linkify: false,
  typographer: true,
  highlight: highlight
};

/**
 * Used to add locale specific overrides when required. See avaiable settings
 * in the MarkdownSetting type.
 *
 * @example
 * {
 *   'ex-EX': {
 *     ...defaultSettings,
 *     quotes: '«»„“',
 *   },
 * }
 *
 * @type {[key: string]: MarkdownSetting}
 */
const localeSettings = {};

/**
 * Create a new MarkdownIt instance using the provided settings and adding additional plugins.
 *
 * @param {MarkdownSetting} settings The settings that the instance should be created with
 * @param {string} locale The language that this renderer should be in. To support smart quotes
 * @param {function} onTocUpdate Method to set the table of contents array
 * @param {string} theme The markdown styles theme.
 * @returns {MarkdownIt} The instance that can be used to render markdown
 */
const createInstance = (settings, locale, onTocUpdate, theme) => {
  const instance = new MarkdownIt({
    ...settings,
    ...defaultSettings,
    theme
  });
  rendererPlugins.forEach(plugin => plugin(instance.renderer, locale, theme));
  usePlugins.forEach(plugin => {
    // Production build won't have plugin names
    // onTocUpdate function needs to be sent to all plugins
    // even though it will only be used with TableOfContents plugin
    const pluginAndOptions = plugin(onTocUpdate);
    instance.use(pluginAndOptions.plugin, pluginAndOptions.options);
  });
  return instance;
};

/**
 * Creates an instance of MarkdownIt that can render markdown using smart quotes in the provided language.
 * Falls back to default settings if the locale is not found.
 *
 * @param {string} locale The language that this renderer should be in. To support smart quotes
 * @param {function} onTocUpdate Method to set the table of contents array
 * @param {string} theme The markdown styles theme.
 * @returns {MarkdownIt} The instance of the renderer
 */
const getMarkdownRenderer = (locale, onTocUpdate, theme) => {
  if (localeSettings[locale]) {
    return createInstance(localeSettings[locale], locale, onTocUpdate, theme);
  } else {
    return createInstance(defaultSettings[locale], locale, onTocUpdate, theme);
  }
};

/**
 * Renders HTML from markdown content.
 *
 * @param {string} [markdown=''] The markdown that you want to render.
 * @param {string} [locale='en-US'] The language that markdown is in. This will help the smart quotes look right.
 * @param {callback} [onTocUpdate] Callback function to update table of content
 * @param {string} [theme='light'] The markdown styles theme.
 * @returns {string}
 * @throws {Error} When the markdown contains forbidden HTML
 */
const renderHtml = function () {
  let markdown = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_LOCALE;
  let onTocUpdate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : () => null;
  let theme = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : DEFAULT_THEME;
  return getMarkdownRenderer(locale, onTocUpdate, theme).render(markdown);
};

/**
 * Generates a sanitized HTML from markdown content.
 *
 * @param {string} [markdown=''] The markdown that you want to render.
 * @param {string} [locale='en-US'] The language that markdown is in. This will help the smart quotes look right.
 * @param {callback} [onTocUpdate] Callback function to update table of content
 * @param {string} [theme='light'] The markdown styles theme.
 * @returns {string}
 * @throws {Error} When the markdown contains forbidden HTML
 */
const generateHtml = function () {
  let markdown = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_LOCALE;
  let onTocUpdate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : () => null;
  let theme = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : DEFAULT_THEME;
  if (!markdown) return;
  const origHtml = renderHtml(markdown, locale, onTocUpdate, theme);
  return sanitizeHtml(origHtml);
};

export { generateHtml, renderHtml };
